import { useEffect, useState } from 'react';
import { Palette, Stamp, Quote, Save, Loader2, Image, RefreshCw } from 'lucide-react';
import { apiFetch } from '../lib/api';

interface Illustration {
  id: number;
  title: string;
  imageUrl: string;
  sortOrder: number;
}

interface QuoteItem {
  id: number;
  content: string;
  author: string;
}

interface CardSettings {
  stampMain: string;
  stampSub: string;
  selectedIllustrationId: number | null;
  selectedQuoteId: number | null;
  customQuote: string;
  customAuthor: string;
  cardTitle: string;
  cardSubtitle: string;
}

export default function AdminAhJimStudio() {
  const [illustrations, setIllustrations] = useState<Illustration[]>([]);
  const [quotes, setQuotes] = useState<QuoteItem[]>([]);
  const [settings, setSettings] = useState<CardSettings>({
    stampMain: '',
    stampSub: '',
    selectedIllustrationId: null,
    selectedQuoteId: null,
    customQuote: '',
    customAuthor: '',
    cardTitle: '感謝您！',
    cardSubtitle: '改寫了兩個人生',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    const [illusRes, quotesRes, settingsRes] = await Promise.all([
      apiFetch<Illustration[]>('/api/admin/ahjim-illustrations'),
      apiFetch<QuoteItem[]>('/api/admin/ahjim-quotes'),
      apiFetch<Record<string, string>>('/api/public/settings'),
    ]);
    if (illusRes.success && illusRes.data) setIllustrations(illusRes.data);
    if (quotesRes.success && quotesRes.data) setQuotes(quotesRes.data);
    if (settingsRes.success && settingsRes.data) {
      const s = settingsRes.data;
      setSettings((prev) => ({
        ...prev,
        stampMain: s.ahjimStampMain || '',
        stampSub: s.ahjimStampSub || '',
        selectedIllustrationId: s.cardIllustrationId ? Number(s.cardIllustrationId) : null,
        selectedQuoteId: s.cardQuoteId ? Number(s.cardQuoteId) : null,
        customQuote: s.cardCustomQuote || '',
        customAuthor: s.cardCustomAuthor || '',
        cardTitle: s.cardTitle || '感謝您！',
        cardSubtitle: s.cardSubtitle || '改寫了兩個人生',
      }));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const res = await apiFetch('/api/admin/settings', {
      method: 'PUT',
      body: JSON.stringify({
        ahjimStampMain: settings.stampMain,
        ahjimStampSub: settings.stampSub,
        cardIllustrationId: settings.selectedIllustrationId ? String(settings.selectedIllustrationId) : '',
        cardQuoteId: settings.selectedQuoteId ? String(settings.selectedQuoteId) : '',
        cardCustomQuote: settings.customQuote,
        cardCustomAuthor: settings.customAuthor,
        cardTitle: settings.cardTitle,
        cardSubtitle: settings.cardSubtitle,
      }),
    });
    setSaving(false);
    if (res.success) {
      alert('圖樣設置已保存');
    } else {
      alert(res.error?.message || '保存失敗');
    }
  };

  const selectedIllus = illustrations.find((i) => i.id === settings.selectedIllustrationId);
  const selectedQuote = quotes.find((q) => q.id === settings.selectedQuoteId);
  const displayQuote = selectedQuote ? selectedQuote.content : settings.customQuote;
  const displayAuthor = selectedQuote ? selectedQuote.author : settings.customAuthor;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-brand-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Palette className="w-6 h-6 text-brand-600" />
          阿占語錄 / 圖樣製作室
        </h1>
        <button onClick={handleSave} disabled={saving} className="btn-primary py-2.5 px-5 min-h-[44px]">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          <Save className="w-4 h-4" />
          {saving ? '保存中...' : '保存設置'}
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: Settings */}
        <div className="space-y-6">
          {/* Stamp Settings */}
          <div className="card shadow-sm border border-brand-100">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <Stamp className="w-4 h-4 text-brand-600" />
              <h3 className="font-bold text-gray-800">郵戳蓋章</h3>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">主文字（郵戳中間）</label>
                <input type="text" value={settings.stampMain} onChange={(e) => setSettings((s) => ({ ...s, stampMain: e.target.value }))} className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 outline-none text-sm" placeholder="支持 S2C" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">副文字（郵戳下方）</label>
                <input type="text" value={settings.stampSub} onChange={(e) => setSettings((s) => ({ ...s, stampSub: e.target.value }))} className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 outline-none text-sm" placeholder="傳愛英雄" />
              </div>
            </div>
          </div>

          {/* Card Text */}
          <div className="card shadow-sm border border-brand-100">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <Quote className="w-4 h-4 text-brand-600" />
              <h3 className="font-bold text-gray-800">卡片文字</h3>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">大標題</label>
                  <input type="text" value={settings.cardTitle} onChange={(e) => setSettings((s) => ({ ...s, cardTitle: e.target.value }))} className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 outline-none text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">副標題</label>
                  <input type="text" value={settings.cardSubtitle} onChange={(e) => setSettings((s) => ({ ...s, cardSubtitle: e.target.value }))} className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 outline-none text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">語錄選擇</label>
                <select
                  value={settings.selectedQuoteId ?? ''}
                  onChange={(e) => setSettings((s) => ({ ...s, selectedQuoteId: e.target.value ? Number(e.target.value) : null }))}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 outline-none text-sm bg-white mb-2"
                >
                  <option value="">使用自訂語錄</option>
                  {quotes.map((q) => (
                    <option key={q.id} value={q.id}>{q.content.slice(0, 30)}... — {q.author}</option>
                  ))}
                </select>
                {settings.selectedQuoteId === null && (
                  <div className="space-y-2">
                    <textarea value={settings.customQuote} onChange={(e) => setSettings((s) => ({ ...s, customQuote: e.target.value }))} rows={2} className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 outline-none text-sm" placeholder="自訂語錄內容..." />
                    <input type="text" value={settings.customAuthor} onChange={(e) => setSettings((s) => ({ ...s, customAuthor: e.target.value }))} className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 outline-none text-sm" placeholder="作者名稱..." />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Illustration Selection */}
          <div className="card shadow-sm border border-brand-100">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <Image className="w-4 h-4 text-brand-600" />
              <h3 className="font-bold text-gray-800">背景插圖</h3>
            </div>
            <div className="p-4">
              {illustrations.length === 0 ? (
                <p className="text-sm text-gray-400">暫無插圖，請先到「阿占插圖」上傳</p>
              ) : (
                <div className="grid grid-cols-4 gap-3">
                  {illustrations.map((illus) => (
                    <button
                      key={illus.id}
                      onClick={() => setSettings((s) => ({ ...s, selectedIllustrationId: illus.id }))}
                      className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                        settings.selectedIllustrationId === illus.id
                          ? 'border-brand-500 ring-2 ring-brand-200'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <img src={illus.imageUrl} alt={illus.title} className="w-full h-full object-cover" />
                      {settings.selectedIllustrationId === illus.id && (
                        <div className="absolute inset-0 bg-brand-500/20 flex items-center justify-center">
                          <div className="bg-brand-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">已選</div>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: Preview */}
        <div>
          <div className="sticky top-6 space-y-4">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <RefreshCw className="w-4 h-4" />
              即時預覽（捐款成功卡效果）
            </div>
            <div className="bg-cream rounded-3xl p-6 shadow-lg border border-orange-100">
              {/* Card Preview */}
              <div className="bg-white rounded-2xl p-6 text-center shadow-sm border border-orange-100 relative overflow-hidden">
                {/* Background Illustration */}
                {selectedIllus && (
                  <div className="absolute inset-0 opacity-10">
                    <img src={selectedIllus.imageUrl} alt="" className="w-full h-full object-cover" />
                  </div>
                )}

                {/* Stamp */}
                {(settings.stampMain || settings.stampSub) && (
                  <div className="relative mb-4">
                    <div className="w-20 h-20 mx-auto rounded-full border-4 border-dashed border-red-400 flex flex-col items-center justify-center bg-red-50/80 rotate-[-12deg]">
                      <span className="text-red-600 font-bold text-xs leading-tight">{settings.stampMain || '支持'}</span>
                      <span className="text-red-500 text-[10px] leading-tight">{settings.stampSub || 'S2C'}</span>
                    </div>
                  </div>
                )}

                {/* Title */}
                <h2 className="text-2xl font-bold text-gray-900 mb-1 relative">{settings.cardTitle}</h2>
                <p className="text-xl font-bold text-gray-900 mb-2 relative">{settings.cardSubtitle}</p>
                <p className="text-gray-500 text-sm mb-4 relative">您的愛心將化作一頓頓溫飽的飯餐，溫暖每一個需要幫助的人</p>

                {/* Donation Info Box */}
                <div className="bg-brand-50 rounded-2xl p-4 mb-4 border border-orange-100 relative">
                  <div className="text-sm text-gray-500 mb-1">捐款編號</div>
                  <div className="text-xl font-bold text-brand-800 mb-2">DN-DEMO-001</div>
                  <div className="text-sm text-gray-500 mb-1">捐款金額</div>
                  <div className="text-3xl font-bold text-brand-600">HK$500</div>
                  <div className="mt-3 pt-3 border-t border-orange-200/60 text-sm text-gray-600 space-y-1">
                    <div className="flex items-center justify-center gap-2"><span>🍚</span><span>為基層人士提供 <strong className="text-brand-700">10</strong> 份飯餐</span></div>
                    <div className="flex items-center justify-center gap-2"><span>👦</span><span>支持 SEN 學員 <strong className="text-brand-700">5</strong> 小時職訓</span></div>
                  </div>
                </div>

                {/* Quote */}
                {displayQuote && (
                  <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 relative">
                    <p className="text-gray-700 text-sm italic leading-relaxed">「{displayQuote}」</p>
                    {displayAuthor && <p className="text-xs text-gray-400 mt-1">— {displayAuthor}</p>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
